import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ImportResult, TenantCustomField } from '../types';
import { apiService } from '../services/api';
import { Header } from '../components/Header';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';

const BREADCRUMBS = [
  { label: 'Home', href: '/' },
  { label: 'Importar Contatos', href: '/contatos/importar' },
  { label: 'Mapeamento de Campos' },
];

interface SystemField {
  key: string;
  label: string;
  required: boolean;
  type: 'system' | 'custom';
}

interface FieldMapping {
  systemField: string;
  fileColumn: string;
  required: boolean;
}

export function ContactsImportMappingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { file, categoryId, duplicateAction, duplicateCategoriaId } = location.state || {};

  const [columns, setColumns] = useState<string[]>([]);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [mappings, setMappings] = useState<FieldMapping[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(true);
  const [customFields, setCustomFields] = useState<TenantCustomField[]>([]);
  const [systemFields, setSystemFields] = useState<SystemField[]>([]);

  useEffect(() => {
    if (!file) {
      toast.error('Nenhum arquivo selecionado');
      navigate('/contatos/importar');
      return;
    }

    initializeData();
  }, [file]);

  const initializeData = async () => {
    setIsLoadingPreview(true);
    try {
      // Carregar campos personalizados primeiro
      const fields = await apiService.getCustomFields();
      setCustomFields(fields);

      // Criar lista de campos do sistema + campos personalizados
      const baseFields: SystemField[] = [
        { key: 'nome', label: 'Nome', required: true, type: 'system' },
        { key: 'telefone', label: 'Telefone', required: true, type: 'system' },
        { key: 'email', label: 'Email', required: false, type: 'system' },
        { key: 'observacoes', label: 'Observações', required: false, type: 'system' },
        { key: 'tags', label: 'Tags', required: false, type: 'system' },
      ];

      const customFieldsMapped = fields.map(f => ({
        key: `custom_${f.key}`,
        label: f.label,
        required: f.required,
        type: 'custom' as const
      }));

      const allFields = [...baseFields, ...customFieldsMapped];
      setSystemFields(allFields);

      // Agora carregar preview do arquivo
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiService.previewCSV(formData);

      setColumns(response.columns || []);
      setPreviewData(response.preview || []);

      // Inicializar mapeamentos com os campos já carregados
      initializeMappings(response.columns || [], allFields);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao processar arquivo');
      navigate('/contatos/importar');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const initializeMappings = (fileColumns: string[], fields: SystemField[]) => {
    // Primeiro, tentar auto-mapear
    const initialMappings = fields.map(sysField => {
      const colLower = sysField.key.toLowerCase();
      let matchedColumn = '';

      // Tentar encontrar coluna correspondente no arquivo
      for (const fileCol of fileColumns) {
        const fileColLower = fileCol.toLowerCase().trim();

        if (sysField.key === 'nome' && (fileColLower === 'nome' || fileColLower === 'name')) {
          matchedColumn = fileCol;
          break;
        }
        if (sysField.key === 'telefone' && (fileColLower === 'telefone' || fileColLower === 'phone' || fileColLower === 'celular')) {
          matchedColumn = fileCol;
          break;
        }
        if (sysField.key === 'email' && (fileColLower === 'email' || fileColLower === 'e-mail')) {
          matchedColumn = fileCol;
          break;
        }
        if (sysField.key === 'observacoes' && (fileColLower === 'observacoes' || fileColLower === 'observações' || fileColLower === 'obs' || fileColLower === 'notes')) {
          matchedColumn = fileCol;
          break;
        }
        if (sysField.key === 'tags' && (fileColLower === 'tags' || fileColLower === 'etiquetas')) {
          matchedColumn = fileCol;
          break;
        }

        // Para campos personalizados, tentar match pelo label ou key
        if (sysField.type === 'custom') {
          const customKey = sysField.key.replace('custom_', '');
          if (fileColLower === sysField.label.toLowerCase() || fileColLower === customKey.toLowerCase()) {
            matchedColumn = fileCol;
            break;
          }
        }
      }

      return {
        systemField: sysField.key,
        fileColumn: matchedColumn,
        required: sysField.required
      };
    });

    setMappings(initialMappings);
  };

  const handleMappingChange = (systemField: string, fileColumn: string) => {
    setMappings(prev =>
      prev.map(m =>
        m.systemField === systemField
          ? { ...m, fileColumn }
          : m
      )
    );
  };

  const validateMappings = (): boolean => {
    // Verificar campos obrigatórios
    const missingRequired = mappings.filter(m => m.required && !m.fileColumn);

    if (missingRequired.length > 0) {
      const fieldLabels = missingRequired.map(m => {
        const field = systemFields.find(f => f.key === m.systemField);
        return field?.label || m.systemField;
      }).join(', ');
      toast.error(`Campos obrigatórios não mapeados: ${fieldLabels}`);
      return false;
    }

    // Verificar duplicatas
    const usedColumns = mappings
      .filter(m => m.fileColumn && m.fileColumn !== '')
      .map(m => m.fileColumn);

    const duplicates = usedColumns.filter((col, index) => usedColumns.indexOf(col) !== index);

    if (duplicates.length > 0) {
      toast.error('Você não pode mapear a mesma coluna do arquivo para múltiplos campos');
      return false;
    }

    return true;
  };

  const handleImport = async () => {
    if (!validateMappings()) return;

    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Converter mappings para formato esperado pelo backend
      const backendMappings = mappings
        .filter(m => m.fileColumn)
        .map(m => ({
          fileColumn: m.fileColumn,
          systemField: m.systemField
        }));

      formData.append('mappings', JSON.stringify(backendMappings));
      if (categoryId) formData.append('categoriaId', categoryId);
      if (duplicateAction) formData.append('duplicateAction', duplicateAction);
      if (duplicateCategoriaId) formData.append('duplicateCategoriaId', duplicateCategoriaId);

      const result = await apiService.importCSVWithMapping(formData);

      if (result.success) {
        const parts = [`${result.successfulImports} importados`];
        if (result.skippedDuplicates) parts.push(`${result.skippedDuplicates} duplicados pulados`);
        if (result.updatedDuplicates) parts.push(`${result.updatedDuplicates} duplicados atualizados`);
        toast.success(`Importação concluída! ${parts.join(', ')}.`);
        navigate('/contatos');
      } else {
        toast(`Importação parcial: ${result.successfulImports} sucessos, ${result.failedImports} erros.`, {
          icon: '⚠️',
        });
      }
    } catch (error) {
      console.error('Erro na importação:', error);
      toast.error('Erro ao importar arquivo');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoadingPreview) {
    return (
      <>
        <Header title="Mapeamento de Campos" subtitle="Carregando preview do arquivo..." compactTopbar />
        <PageLayout>
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Processando arquivo...</p>
            </div>
          </div>
        </PageLayout>
      </>
    );
  }

  const getPreviewValue = (fileColumn: string) => {
    if (!fileColumn || !previewData[0]) return '-';
    return previewData[0][fileColumn] || '-';
  };

  const getFieldLabel = (systemField: string) => {
    const field = systemFields.find(f => f.key === systemField);
    return field?.label || systemField;
  };

  const isFieldIgnored = (mapping: FieldMapping) => {
    return !mapping.fileColumn || mapping.fileColumn === '';
  };

  return (
    <>
      <Header
        title="Mapeamento de Campos"
        subtitle="Relacione os campos do sistema com as colunas do arquivo"
        compactTopbar
      />

      <PageLayout>
        <PageHeader title="Mapeamento de Campos" breadcrumbs={BREADCRUMBS} />

        <div className="space-y-8 w-full">
          {/* Card de Mapeamento */}
          <div className="card-padrao overflow-hidden">
            {/* Cabeçalho */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-4">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">
                Mapeamento de Campos
              </h2>
              <button
                type="button"
                onClick={handleImport}
                disabled={isProcessing}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-transparent text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Importando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-file-import text-xl shrink-0" />
                    Importar Contatos
                  </>
                )}
              </button>
            </div>

            <div className="p-5 sm:p-6 space-y-6">
              {/* Instruções */}
              <div className="rounded-xl border p-4 border-blue-500 bg-blue-50 dark:border-blue-500/30 dark:bg-blue-500/15">
                <div className="flex items-start gap-3">
                  <div className="-mt-0.5 text-blue-500">
                    <svg className="fill-current" width="24" height="24" viewBox="0 0 24 24">
                      <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                    </svg>
                  </div>
                  <div>
                    <h4 className="mb-1 text-sm font-semibold text-gray-800 dark:text-white/90">Instruções</h4>
                    <ul className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                      <li>• Selecione a coluna do arquivo para cada campo do sistema</li>
                      <li>• Campos com <span className="text-red-600 dark:text-red-400 font-bold">*</span> são obrigatórios</li>
                      <li>• Selecione "Ignorar" para não importar um campo</li>
                      <li>• Verifique o preview abaixo para confirmar os dados</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Tabela de Mapeamento */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Campo do Sistema
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Coluna do Arquivo
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Preview
                      </th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300 w-24">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappings.map((mapping, index) => {
                      const ignored = isFieldIgnored(mapping);
                      return (
                        <tr
                          key={mapping.systemField}
                          className={`border-b border-gray-100 dark:border-gray-800 transition-opacity ${ignored ? 'opacity-40' : 'opacity-100'}`}
                        >
                          <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white">
                            <div className="flex items-center gap-1">
                              <span className={ignored ? 'line-through' : ''}>
                                {getFieldLabel(mapping.systemField)}
                              </span>
                              {mapping.required && (
                                <span className="text-red-600 dark:text-red-400 text-sm font-bold">
                                  *
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <select
                              value={mapping.fileColumn}
                              onChange={(e) => handleMappingChange(mapping.systemField, e.target.value)}
                              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                            >
                              <option value="">Ignorar</option>
                              {columns.map(col => (
                                <option key={col} value={col}>
                                  {col}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            <div className="max-w-xs truncate">
                              {getPreviewValue(mapping.fileColumn)}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            {!mapping.required && (
                              <button
                                type="button"
                                onClick={() => handleMappingChange(mapping.systemField, '')}
                                disabled={ignored}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                title={ignored ? 'Campo já ignorado' : 'Ignorar campo'}
                              >
                                <i className="fas fa-ban text-sm" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Preview dos dados */}
              {previewData.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90 mb-3">
                    Preview das primeiras linhas
                  </h3>
                  <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          {columns.map(col => (
                            <th key={col} className="py-2 px-4 text-left font-medium text-gray-700 dark:text-gray-300">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.slice(0, 3).map((row, rowIndex) => (
                          <tr key={rowIndex} className="border-t border-gray-100 dark:border-gray-800">
                            {columns.map(col => (
                              <td key={col} className="py-2 px-4 text-gray-600 dark:text-gray-400">
                                {row[col] || '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Botões de ação */}
          <div className="flex justify-between items-center">
            <button
              type="button"
              onClick={() => navigate('/contatos/importar')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:border-transparent transition-colors"
            >
              <i className="fas fa-arrow-left text-base shrink-0" />
              Voltar
            </button>

            <button
              type="button"
              onClick={handleImport}
              disabled={isProcessing}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-transparent text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Importando...
                </>
              ) : (
                <>
                  <i className="fas fa-file-import text-xl shrink-0" />
                  Importar Contatos
                </>
              )}
            </button>
          </div>
        </div>
      </PageLayout>
    </>
  );
}
